import React from 'react';
import type { ImmediateNextStep } from '../services/useCaseExplorerService';

export interface StepListProps {
  /** Hierarchical steps (preferred) or legacy flat strings. */
  steps: ImmediateNextStep[] | string[];
}

function toGroups(steps: ImmediateNextStep[] | string[]): ImmediateNextStep[] {
  if (steps.length === 0) return [];
  if (typeof steps[0] === 'string') {
    return (steps as string[])
      .map((title) => title.trim())
      .filter(Boolean)
      .map((title) => ({ title, items: [] }));
  }
  return steps as ImmediateNextStep[];
}

export const StepList: React.FC<StepListProps> = ({ steps }) => {
  const groups = toGroups(steps);

  if (groups.length === 0) {
    return (
      <p className="text-sm text-[#717182] leading-relaxed italic">
        No next steps are documented for this use case yet.
      </p>
    );
  }

  return (
    <ol className="space-y-3.5">
      {groups.map((group, i) => (
        <li key={`${i}-${group.title.slice(0, 32)}`} className="flex items-start gap-2.5">
          <span className="w-5 h-5 rounded-full bg-[#FF6E4C] text-white text-[10px] font-bold flex items-center justify-center shrink-0 mt-0.5">
            {i + 1}
          </span>
          <div className="min-w-0 flex-1">
            <p
              className={`text-sm leading-relaxed ${
                group.items.length > 0
                  ? 'font-semibold text-[#18365E]'
                  : 'text-[#4b5563]'
              }`}
            >
              {group.title}
            </p>
            {group.items.length > 0 && (
              <ul className="mt-1.5 space-y-1.5 border-l-2 border-[#e5e7eb] pl-3">
                {group.items.map((item, j) => (
                  <li
                    key={`${i}-${j}-${item.slice(0, 24)}`}
                    className="flex items-start gap-2 text-sm text-[#4b5563] leading-relaxed"
                  >
                    <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-[#9ca3af]" aria-hidden />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </li>
      ))}
    </ol>
  );
};

export default StepList;
